'use client';

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@prismo-io/design-system';
import { useMe } from '@prismo-io/feature-auth/providers/me';
import { Application, ButtonLink, translate } from '@prismo-io/router';
import {
  AuraProfileCode,
  AuraResultStatus,
  LanguageCode,
  type LanguageCodeT,
} from '@prismo-io/schemas';
import Image from 'next/image';
import Link from 'next/link';
import { type FC, useMemo } from 'react';
import type { UserProfileCardValueSchemaT } from '../../../utils/fetch-user-profile-card-data';

import AdvisorIllustration from '@/assets/aura/app/advisor/common/aura-profile.svg';
import AnalystIllustration from '@/assets/aura/app/analyst/common/aura-profile.svg';
import AnimatorIllustration from '@/assets/aura/app/animator/common/aura-profile.svg';
import CoachIllustration from '@/assets/aura/app/coach/common/aura-profile.svg';
import PeacemakerIllustration from '@/assets/aura/app/peacemaker/common/aura-profile.svg';
import PilotIllustration from '@/assets/aura/app/pilot/common/aura-profile.svg';
import PlannerIllustration from '@/assets/aura/app/planner/common/aura-profile.svg';
import ProtectorIllustration from '@/assets/aura/app/protector/common/aura-profile.svg';
import DefaultIllustration from '@/assets/logos/aura/square-dark.svg';
import { Countdown } from '../../count-down';

type Props = {
  order: number;
  userUrl: string;
  result: UserProfileCardValueSchemaT;
  canAccessLockedResult: boolean;
};

const buildAuraUrl = (locale: LanguageCodeT) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.AURA,
    path: '/inscription',
    addHostname: true,
    locale,
  });
};

export const AuraCard: FC<Props> = (props: Props) => {
  const { order, userUrl, result, canAccessLockedResult } = props;

  const me = useMe();

  const isMe = useMemo(() => me && me.url === userUrl, [me, userUrl]);

  const isLocked = useMemo(
    () =>
      result
        ? result.status === AuraResultStatus.Enum.LOCKED &&
          !canAccessLockedResult
        : false,
    [result, canAccessLockedResult]
  );

  const profileType = useMemo(
    () => (isLocked ? undefined : result?.adapted_profile?.id),
    [result, isLocked]
  );

  const profileName = useMemo(
    () => (isLocked ? undefined : result?.adapted_profile?.translate?.name),
    [result, isLocked]
  );

  const img = useMemo(() => {
    switch (profileType) {
      case AuraProfileCode.Enum.ADVISOR:
        return AdvisorIllustration;
      case AuraProfileCode.Enum.ANALYST:
        return AnalystIllustration;
      case AuraProfileCode.Enum.ANIMATOR:
        return AnimatorIllustration;
      case AuraProfileCode.Enum.COACH:
        return CoachIllustration;
      case AuraProfileCode.Enum.PEACEMAKER:
        return PeacemakerIllustration;
      case AuraProfileCode.Enum.PILOT:
        return PilotIllustration;
      case AuraProfileCode.Enum.PLANNER:
        return PlannerIllustration;
      case AuraProfileCode.Enum.PROTECTOR:
        return ProtectorIllustration;
      default:
        return DefaultIllustration;
    }
  }, [profileType]);

  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;
  const auraUrl = buildAuraUrl(locale as LanguageCodeT);

  return (
    <Stack
      component="article"
      bgcolor={profileType ? 'background.primary' : 'white'}
      borderRadius="3rem"
      p={2}
      zIndex={10}
      minWidth={{ none: '100%', sm: '45%', lg: '31%' }}
      sx={{
        boxShadow: '2px 2px 7px 0px rgba(0,0,0,0.3)',
        flex: '1 1 0%',
        order: order,
      }}
      direction="column"
      alignItems="center"
      spacing={2}
    >
      <Box component="div" position="relative" width="10.5rem" height="10.5rem">
        <Image
          src={img}
          alt={t`Profil Adapté Aura | PRISMO`}
          style={{ objectFit: 'contain', objectPosition: 'center' }}
          fill
        />
      </Box>
      {profileType && (
        <>
          <Stack direction="row" gap="3px">
            <Typography variant="h3">
              <Trans>Aura</Trans>
            </Typography>
            <Typography variant="h3">
              <Trans>|</Trans>
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'normal' }}>
              <Trans>Profil comportemental :</Trans>
            </Typography>
          </Stack>
          <Box
            component="div"
            width="fit-content"
            marginX="auto"
            bgcolor="background.primary"
            borderRadius={1_000_000}
          >
            <Typography
              color="primary.main"
              textAlign="center"
              paddingY={1}
              paddingX={2}
              variant="h4"
              fontWeight="bold"
            >
              {profileName}
            </Typography>
          </Box>
          <Box component="div">
            <ButtonLink
              app={Application.APP}
              path="/profil/:user/comportement-aura"
              params={{ user: userUrl }}
              prefetch={false}
              variant="text"
              color="inherit"
              sx={{
                p: 0,
                '& .MuiButton-startIcon': {
                  display: 'none',
                },
              }}
            >
              <Trans>En savoir plus</Trans>
            </ButtonLink>
          </Box>
        </>
      )}
      {!profileType && isLocked && isMe && (
        <Typography variant="h3" textAlign="center">
          {result?.unlocked_at instanceof Date ? (
            <Countdown endDate={result.unlocked_at} />
          ) : (
            <Trans>
              Résultats en attente de déblocage par votre administrateur.
            </Trans>
          )}
        </Typography>
      )}
      {!profileType && !isLocked && isMe && (
        <>
          <Box component="div">
            <Button
              LinkComponent={Link}
              href={auraUrl}
              variant="contained"
              color="gradient"
              sx={{
                '& .MuiButton-startIcon': {
                  display: 'none',
                },
              }}
            >
              <Trans>Passer Aura</Trans>
            </Button>
          </Box>
          <Typography variant="h3">
            <Trans>Identifiez votre profil comportemental en 5 min !</Trans>
          </Typography>
        </>
      )}
      {!profileType && !isMe && (
        <Typography paddingTop={5} variant="h3">
          <Trans>Cet utilisateur n'a pas encore passé Aura !</Trans>
        </Typography>
      )}
    </Stack>
  );
};
